/**
 * NightMonitoringEngine - Night monitoring for EyeControl mode
 * Monitors user wakefulness during night hours and alerts caregiver
 * 
 * Logic:
 * - When user is sleeping, EyeControl sends continuous Enter key press
 * - When Enter is released, start counting awake time
 * - If no Enter press for more than 10 seconds, continue counting
 * - If Enter is pressed for more than 10 seconds, reset awake timer
 * - After configured duration without extended Enter presses, user is "awake"
 * - Send email alert to caregiver
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { getUserSettings } from '@/lib/userSettings';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const ENTER_RELEASE_THRESHOLD = 10000; // 10 seconds in ms

export function NightMonitoringEngine() {
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [awakeSeconds, setAwakeSeconds] = useState(0);
  
  const enterPressedRef = useRef(false);
  const enterPressStartRef = useRef<number>(0);
  const lastEnterReleaseRef = useRef<number>(0);
  const awakeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const alertSentRef = useRef(false);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Check if current time is within night monitoring hours
  const isWithinNightHours = useCallback(() => {
    const settings = getUserSettings();
    if (!settings.nightMonitoringEnabled) return false;
    
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeMinutes = currentHour * 60 + currentMinute;
    
    const [startHour, startMin] = (settings.nightMonitoringStartTime || '22:00').split(':').map(Number);
    const [endHour, endMin] = (settings.nightMonitoringEndTime || '06:00').split(':').map(Number);
    
    const startTimeMinutes = startHour * 60 + startMin;
    const endTimeMinutes = endHour * 60 + endMin;
    
    // Handle overnight range (e.g., 22:00 to 06:00)
    if (startTimeMinutes > endTimeMinutes) {
      return currentTimeMinutes >= startTimeMinutes || currentTimeMinutes < endTimeMinutes;
    }
    
    // Same day range
    return currentTimeMinutes >= startTimeMinutes && currentTimeMinutes < endTimeMinutes;
  }, []);
  
  // Check if EyeControl mode is active
  const isEyeControlMode = useCallback(() => {
    const settings = getUserSettings();
    return settings.controlMethod === 'eye_control';
  }, []);
  
  // Send alert to caregiver
  const sendCaregiverAlert = useCallback(async () => {
    if (alertSentRef.current) return;
    
    const settings = getUserSettings();
    const caregiverEmail = settings.caregiverEmail;
    
    console.log('[NightMonitoring] Sending caregiver alert...');
    alertSentRef.current = true;
    
    // Send email alert if caregiver email is configured
    if (caregiverEmail) {
      try {
        const { error } = await supabase.functions.invoke('send-caregiver-alert', {
          body: {
            caregiverEmail,
            userName: settings.name || 'המשתמש',
            awakeDuration: Math.round(awakeSeconds / 60),
            timestamp: new Date().toISOString(),
          },
        });
        
        if (error) {
          console.error('[NightMonitoring] Email alert failed:', error);
        } else {
          console.log('[NightMonitoring] Email alert sent successfully');
        }
      } catch (error) {
        console.error('[NightMonitoring] Failed to send email alert:', error);
      }
    }
    
    // Send push notification to all registered caregiver devices
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { error } = await supabase.functions.invoke('send-push-alert', {
          body: {
            patientId: user.id,
            alertType: 'night_monitoring',
            message: `${settings.name || 'המשתמש'} ער/ה כבר ${Math.round(awakeSeconds / 60)} דקות`,
          },
        });
        
        if (error) {
          console.error('[NightMonitoring] Push alert failed:', error);
        } else {
          console.log('[NightMonitoring] Push alert sent successfully');
        }
      }
    } catch (error) {
      console.error('[NightMonitoring] Failed to send push alert:', error);
    }
    
    toast.success('התראה נשלחה למטפל/ת');
  }, [awakeSeconds]);
  
  // Reset awake tracking
  const resetAwakeTracking = useCallback(() => {
    setAwakeSeconds(0);
    if (awakeTimerRef.current) {
      clearInterval(awakeTimerRef.current);
      awakeTimerRef.current = null;
    }
  }, []);
  
  // Start awake timer
  const startAwakeTimer = useCallback(() => {
    if (awakeTimerRef.current) return;
    
    console.log('[NightMonitoring] Starting awake timer');
    awakeTimerRef.current = setInterval(() => {
      setAwakeSeconds(prev => prev + 1);
    }, 1000);
  }, []);
  
  // Check awake duration and send alert if needed
  useEffect(() => {
    const settings = getUserSettings();
    const requiredDuration = (settings.nightMonitoringDuration || 5) * 60; // Convert to seconds
    
    if (awakeSeconds >= requiredDuration && isMonitoring && !alertSentRef.current) {
      console.log(`[NightMonitoring] User awake for ${awakeSeconds}s, threshold: ${requiredDuration}s`);
      sendCaregiverAlert();
    }
  }, [awakeSeconds, isMonitoring, sendCaregiverAlert]);
  
  // Handle Enter key events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isMonitoring) return;
      if (e.key !== 'Enter') return;
      
      if (!enterPressedRef.current) {
        enterPressedRef.current = true;
        enterPressStartRef.current = Date.now();
        console.log('[NightMonitoring] Enter pressed');
      }
    };
    
    const handleKeyUp = (e: KeyboardEvent) => {
      if (!isMonitoring) return;
      if (e.key !== 'Enter') return;
      
      if (enterPressedRef.current) {
        const pressDuration = Date.now() - enterPressStartRef.current;
        enterPressedRef.current = false;
        lastEnterReleaseRef.current = Date.now();
        
        console.log(`[NightMonitoring] Enter released after ${pressDuration}ms`);
        
        // If Enter was pressed for more than 10 seconds, reset awake timer
        if (pressDuration > ENTER_RELEASE_THRESHOLD) {
          console.log('[NightMonitoring] Long Enter press detected - user sleeping, resetting timer');
          resetAwakeTracking();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isMonitoring, resetAwakeTracking]);
  
  // Check for awake state (no Enter press for 10+ seconds)
  useEffect(() => {
    if (!isMonitoring) return;
    
    const checkAwakeState = () => {
      const now = Date.now();
      const timeSinceRelease = now - lastEnterReleaseRef.current;
      
      // If Enter is not pressed and it's been more than 10 seconds since last release
      if (!enterPressedRef.current && timeSinceRelease > ENTER_RELEASE_THRESHOLD) {
        // User is awake - start/continue counting
        startAwakeTimer();
      } else if (enterPressedRef.current) {
        // Enter is being pressed - check how long
        const pressDuration = now - enterPressStartRef.current;
        if (pressDuration > ENTER_RELEASE_THRESHOLD) {
          // Long press - user is sleeping
          resetAwakeTracking();
        }
      }
    };
    
    checkIntervalRef.current = setInterval(checkAwakeState, 1000);
    
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [isMonitoring, startAwakeTimer, resetAwakeTracking]);
  
  // Main effect - check if monitoring should be active
  useEffect(() => {
    const checkMonitoringState = () => {
      const settings = getUserSettings();
      const shouldMonitor = 
        settings.nightMonitoringEnabled &&
        isEyeControlMode() &&
        isWithinNightHours() &&
        settings.caregiverEmail;
      
      if (shouldMonitor && !isMonitoring) {
        console.log('[NightMonitoring] Starting monitoring');
        setIsMonitoring(true);
        alertSentRef.current = false;
        lastEnterReleaseRef.current = Date.now();
      } else if (!shouldMonitor && isMonitoring) {
        console.log('[NightMonitoring] Stopping monitoring');
        setIsMonitoring(false);
        resetAwakeTracking();
      }
    };
    
    // Check immediately
    checkMonitoringState();
    
    // Check every minute for time-based changes
    const interval = setInterval(checkMonitoringState, 60000);
    
    return () => {
      clearInterval(interval);
      resetAwakeTracking();
    };
  }, [isMonitoring, isEyeControlMode, isWithinNightHours, resetAwakeTracking]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (awakeTimerRef.current) {
        clearInterval(awakeTimerRef.current);
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, []);
  
  // No visible UI
  return null;
}